import {
  Flex,
  Text,
  Grid,
  GridItem,
  Button,
  Icon,
  useDisclosure,
  Tooltip,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BsPatchQuestionFill } from "react-icons/bs";
import { CheckInScheduleInfoModal } from "./checkInScheduleInfoModal";
import { motion } from "framer-motion";
import { isCheckOutDay } from "../../v2/Logic/checkoutDay";

export const CheckInButton = ({
  isCheckInAvailable,
  handleSelect,
  label = "Check\u00A0In\u00A0Instructions",
}) => {
  const MotionBox = motion(Flex);
  const lively = "linear-gradient(270deg,#8B5CF6,#7C3AED,#6366F1,#A855F7,#8B5CF6)";

  if (!isCheckInAvailable) {
    return (
      <Button
        w="full"
        h="42px"
        fontSize="14px"
        color="white"
        borderRadius="10px"
        bg="#5f5ea3"
        disabled={true}
      >
        {label}
      </Button>
    );
  }

  return (
    <Flex
      as="button"
      onClick={handleSelect}
      w="full"
      h="42px"
      borderRadius="10px"
      align="center"
      justify="center"
      color="white"
      fontSize="14px"
      fontWeight="600"
      cursor="pointer"
      gap="6px"
      sx={{
        backgroundImage: lively,
        backgroundSize: "300% 300%",
        animation: "checkinGradient 4s ease infinite, checkinGlow 2.5s ease-in-out infinite",
        "@keyframes checkinGradient": {
          "0%": { backgroundPosition: "0% 50%" },
          "50%": { backgroundPosition: "100% 50%" },
          "100%": { backgroundPosition: "0% 50%" },
        },
        "@keyframes checkinGlow": {
          "0%, 100%": { boxShadow: "0 0 0 0 rgba(139,92,246,0)" },
          "50%": { boxShadow: "0 0 18px 4px rgba(139,92,246,0.4)" },
        },
        "&:active": { transform: "scale(0.97)" },
        transition: "transform 0.1s ease",
      }}
    >
      {label}
      <Text
        as="span"
        display="inline-flex"
        alignItems="center"
        sx={{
          animation: "checkinNudge 1.2s ease-in-out infinite",
          "@keyframes checkinNudge": {
            "0%, 100%": { transform: "translateX(0)" },
            "50%": { transform: "translateX(4px)" },
          },
        }}
      >
        ›
      </Text>
    </Flex>
  );
};



export const CheckInComponent = ({ room, booking, accessTimes, manager }) => {
  const [scheduleText, setScheduleText] = useState("Available Now");
  const [isCheckInAvailable, setIsCheckInAvailable] = useState(true);
  const navigate = useNavigate();
  const {
    isOpen: isOpenCheckIn,
    onOpen: onOpenCheckIn,
    onClose: onCloseCheckIn,
  } = useDisclosure();

  const handleSelect = () => {
    // Forward `manager` so Steps.js can hand it back when navigating
    // to /main-v2. The Redux fallback there is incomplete (some
    // dispatch paths wipe `managerName` to undefined), so the footer
    // falls back to "Linkbase" if we let the round-trip rely on it.
    navigate("/steps", {
      state: { room, booking, type: "checkIn", accessTimes, manager },
    });
  };

  const handleSelectCheckOut = () => {
    navigate("/steps", {
      state: { room, booking, type: "checkOut", accessTimes, manager },
    });
  };

  // On the booking's last day — by the property's clock, see checkoutDay.js —
  // this card stops selling check-in (long done) and hands over to check-out.
  // Only when the host has authored check-out steps; otherwise the card stays
  // as-is rather than pointing at an empty flow.
  const showCheckOut =
    isCheckOutDay(booking, room) &&
    Array.isArray(room?.checkOut) &&
    room.checkOut.length > 0;

  // "11:00" -> "11:00 AM" for the card's corner. Falls back to the raw
  // string for anything unexpected.
  const checkOutTimeLabel = (() => {
    const raw = accessTimes?.effectiveCheckOutTime || room?.checkOutTime;
    if (!raw) return "";
    const match = String(raw).match(/^(\d{1,2}):(\d{2})/);
    if (!match) return String(raw);
    const hours = Number(match[1]);
    const suffix = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 === 0 ? 12 : hours % 12;
    return `${displayHours}:${match[2]} ${suffix}`;
  })();

  const calculateTimeToCheckIn = () => {
    const effectiveCheckInTime =
      accessTimes?.effectiveCheckInTime || room?.checkInTime;

    if (
      room &&
      room.checkInSchedule &&
      booking &&
      booking.checkInDate &&
      effectiveCheckInTime
    ) {
      try {
        const now = new Date();
        const schedule = JSON.parse(room.checkInSchedule);

        // Assuming checkInDate and checkInTime are in UTC
        const checkInDateTimeUTC = new Date(
          `${booking.checkInDate}T${effectiveCheckInTime}Z`
        );

        // Convert UTC to local time
        const checkInDateTime = new Date(
          checkInDateTimeUTC.getTime() +
            checkInDateTimeUTC.getTimezoneOffset() * 60000
        );

        const scheduledTime = new Date(
          checkInDateTime.getTime() -
            schedule.time * (schedule.unit === "days" ? 24 : 1) * 60 * 60 * 1000
        );

        const diffTime = scheduledTime - now;

        if (scheduledTime > now) {
          const diffHours = Math.ceil(diffTime / (60 * 60 * 1000));
          const diffDays = Math.floor(diffTime / (24 * 60 * 60 * 1000)); // Use floor for days to avoid rounding up too early
          let timeString;

          if (diffHours >= 24) {
            timeString = `${diffDays} day${diffDays !== 1 ? "s" : ""}`; // Pluralize correctly
          } else {
            timeString = `${diffHours} hour${diffHours !== 1 ? "s" : ""}`; // Pluralize correctly
          }

          setScheduleText(`Available in ${timeString}`);
          setIsCheckInAvailable(false);
        } else {
          setScheduleText("Available Now");
          setIsCheckInAvailable(true);
        }
      } catch (e) {
        console.error("Error calculating time to check-in:", e);
      }
    }
  };

  useEffect(() => {
    calculateTimeToCheckIn();
  }, [room, booking, accessTimes]);

  const MotionButton = motion(Button);

  // Same card, check-out flavour: no availability schedule (check-out has
  // none) and no schedule info modal, just the time and the instructions.
  if (showCheckOut) {
    return (
      <Flex
        p={3}
        mb={4}
        flexDirection="column"
        bg="#2D3748"
        borderRadius="10px"
      >
        <Flex justifyContent="space-between">
          <Text fontSize="12px" fontWeight={800} color="white">
            Check Out
          </Text>
          {checkOutTimeLabel && (
            <Text fontSize="12px" fontWeight={800} color="white">
              By {checkOutTimeLabel}
            </Text>
          )}
        </Flex>

        <Grid templateColumns="1fr" mt={4}>
          <GridItem>
            <CheckInButton
              isCheckInAvailable={true}
              handleSelect={handleSelectCheckOut}
              label={"Check\u00A0Out\u00A0Instructions"}
            />
          </GridItem>
        </Grid>
      </Flex>
    );
  }

  return (
    <>
      <Flex
        p={3}
        mb={4}
        flexDirection="column"
        bg="#2D3748"
        borderRadius="10px"
      >
        <Flex justifyContent="space-between">
          <Text fontSize="12px" fontWeight={800} color="white">
            Check In
          </Text>
          <Text fontSize="12px" fontWeight={800} color="white">
            {scheduleText}
          </Text>
        </Flex>

        <Grid templateColumns="85% 15%" mt={4}>
          <GridItem mr="5px">
            {room.id === "53a4a418-30ce-4737-9e1a-83463e988753" ? (
              <MotionButton
                onClick={isCheckInAvailable ? handleSelect : undefined}
                bg={isCheckInAvailable ? "#817efc" : "#5f5ea3"}
                color="white"
                h="42px"
                w="full"
                variant="solid"
                borderRadius="10px"
                fontSize="14px"
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                }}
                transition={{
                  duration: 10,
                  ease: "easeInOut",
                  loop: Infinity,
                }}
                background={
                  isCheckInAvailable
                    ? "linear-gradient(270deg, #ff7ea9, #af0e44, #ff6498)"
                    : "linear-gradient(270deg, #511529, #511529, #511529)"
                }
                backgroundSize="200% 200%"
                disabled={!isCheckInAvailable}
              >
                Check In
              </MotionButton>
            ) : (
              <CheckInButton
                isCheckInAvailable={isCheckInAvailable}
                handleSelect={handleSelect}
              />
              // <Button
              //   onClick={isCheckInAvailable ? handleSelect : undefined}
              //   bg={isCheckInAvailable ? "#817efc" : "#5f5ea3"}
              //   color="white"
              //   h="42px"
              //   w="full"
              //   variant="solid"
              //   borderRadius="10px"
              //   fontSize="14px"
              //   disabled={!isCheckInAvailable}
              // >
              //   Check In
              // </Button>
            )}
          </GridItem>
          <GridItem>
            {room.id === "53a4a418-30ce-4737-9e1a-83463e988753" ? (
              <MotionButton
                onClick={onOpenCheckIn}
                bg="#817efc"
                color="white"
                h="42px"
                w="full"
                variant="solid"
                borderRadius="10px"
                fontSize="14px"
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                }}
                transition={{
                  duration: 10,
                  ease: "easeInOut",
                  loop: Infinity,
                }}
                background="linear-gradient(270deg, #ff7ea9, #d369d0, #ff6498)"
                backgroundSize="200% 200%"
              >
                <Icon as={BsPatchQuestionFill} fontSize="20px" />
              </MotionButton>
            ) : (
              <Button
                onClick={onOpenCheckIn}
                bg="#817efc"
                color="white"
                h="42px"
                w="full"
                variant="solid"
                borderRadius="10px"
                fontSize="14px"
              >
                <Icon as={BsPatchQuestionFill} fontSize="20px" />
              </Button>
            )}
          </GridItem>
        </Grid>

        <Text
          fontSize="10px"
          fontWeight={500}
          color="gray.400"
          textAlign={"center"}
          mt={2}
        >
          Reload the page to update time
        </Text>
      </Flex>

      <CheckInScheduleInfoModal
        onClose={onCloseCheckIn}
        isOpen={isOpenCheckIn}
      />
    </>
  );
};
